import { CreditCardIcon, UsersIcon } from "@heroicons/react/solid";
import { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SubscriptionModal from "../../containers/letsPlay/SubscriptionModal";
import AuthContext from "../context/authContext/AuthContext";
import { ACTIONS } from "../context/authContext/Constants";
import { SubscriptionTierEnum } from "../enums/SubscriptionTier";
import logoImage from "./../../../assets/images/logo.png";
import { AppRoles } from "../enums/role.enum";
import {
    createBridgeToken,
    loggedUserWithStudent,
    readStoredSession,
    setParentProfile,
} from "../services/auth/auth.service";
import { IUser } from "../interfaces/user.interface";
import { useIntercom } from "react-use-intercom";
import useLogoutAllTabs from "../hooks/logoutAllTabs";
import moment from "moment";
import { isB2cParentOrStudent } from "../utils/utils";
import { UserCircleIcon } from "@heroicons/react/solid";
import { LogoutIcon } from "@heroicons/react/outline";
import { MenuItem } from "primereact/menuitem";
import { getPortalText } from "./header.helpers";
import WebLoginOutButtons from "./WebLoginOutButtons";
import { toast } from "react-toastify";

const REACT_APP_PORTAL_URL = process.env[`REACT_APP_PORTAL_URL`];

interface IProps {
    isFreeTrialEnded?: boolean;
    openSideBar: () => void;
}

const Header: FC<IProps> = ({ openSideBar, isFreeTrialEnded }) => {
    const location = useLocation();
    const { state: authState, dispatch } = useContext(AuthContext);
    const { postMessage } = useLogoutAllTabs();
    const { shutdown: shutdownIntercom } = useIntercom();
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [selectedProfile, setSelectedProfile] =
        useState<Partial<IUser | null>>(null);
    const [freeTrialRemainingTime, setFreeTrialRemainingTime] = useState("");
    const subTier = authState?.user?.subscriptionTier;
    const showSubscribeNow =
        isFreeTrialEnded || subTier === SubscriptionTierEnum.FREE_TRIAL;

    const getMenuItems = (isMobile: boolean = false): MenuItem[] => {
        const baseItems: (MenuItem | null)[] = [
            {
                template: () => (
                    <div className="px-6 h-7 flex flex-col gap-1">
                        <p className="text-aha-gray">{authState.user?.role}</p>
                        <p className="text-aha-gray text-xs">
                            {authState.user?.email}
                        </p>
                    </div>
                ),
            },
            isMobile && authState && showSubscribeNow
                ? {
                      className: "h-11",
                      icon: <CreditCardIcon className="h-6 w-6 mr-1" />,
                      label: "Subscribe Now",
                      command: () => handleClickSubscribeNow(),
                  }
                : null,
            isMobile
                ? {
                      className: "h-11",
                      icon: <UsersIcon className="h-6 w-6 mr-1" />,
                      label: getPortalText(authState?.user),
                      command: () => {
                          isFreeTrialEnded && !isB2cParentOrStudent(authState)
                              ? handleClickSubscribeNow()
                              : handleClickPortal("web");
                      },
                  }
                : null,
            {
                className: "h-11",
                icon: <UserCircleIcon className="h-6 w-6 mr-1" />,
                label: "My Account",
                command: () => redirectToAdminPortal("/home/account/profile"),
            },
            {
                icon: <LogoutIcon className="h-6 w-6 mr-1" />,
                label: "Sign Out",
                command: () => {
                    shutdownIntercom();
                    postMessage("logout");
                    dispatch({ type: ACTIONS.LOGOUT });
                },
            },
        ];

        return baseItems.filter(Boolean) as MenuItem[];
    };

    const redirectToAdminPortal = async (redirect?: string | null) => {
        const jwt = readStoredSession();

        const { response, errors } = jwt?.accessToken
            ? await createBridgeToken(jwt.accessToken)
            : { response: null, errors: true };

        if (errors || !response?.bridgeToken) {
            toast.error(
                "There was an error redirecting you to the admin portal. Please try again."
            );
            return;
        }

        window.open(
            `${REACT_APP_PORTAL_URL}/auth/bridge-login?id=${
                response?.bridgeToken
            }${redirect ? `&redirect=${redirect}` : ""}`,
            "_blank"
        );
    };

    const handleClickPortal = (type: "web" | "mobile") => {
        if (isFreeTrialEnded) {
            setShowSubscriptionModal(true);
        } else {
            if (type === "mobile") {
                window.open(`${REACT_APP_PORTAL_URL}/auth/login`, "_blank");
            } else {
                redirectToAdminPortal();
            }
        }
    };

    const handleClickSubscribeNow = () => {
        setShowSubscriptionModal(true);
    };

    const getUserFromApi = async () => {
        const { response, errors } = await loggedUserWithStudent();

        if (!errors) {
            const students = response?.parentInfo?.students?.length
                ? response.parentInfo.students.map(
                      (student: Partial<IUser>) => {
                          return {
                              id: student.id,
                              firstname: student.firstname,
                              lastname: student.lastname,
                              fullname: `${student.firstname} ${student.lastname}`,
                              role: AppRoles.STUDENT,
                          };
                      }
                  )
                : [];

            dispatch({
                type: ACTIONS.SET_STUDENTS_UNDER_USER,
                students,
            });

            const parent = {
                id: response.id,
                firstname: response.firstname,
                lastname: response.lastname,
                fullname: `${response.firstname} ${response.lastname}`,
                role: AppRoles.PARENT,
            };

            if (!response.profileStudentUsed) {
                if (students?.length) {
                    setSelectedProfile(students[0]);
                } else {
                    setSelectedProfile(parent);
                }
            }

            if (response?.id !== selectedProfile?.id) {
                if (selectedProfile) {
                    updateProfile(selectedProfile);
                }
            }
        }
    };

    const updateProfile = async (value: Partial<IUser>) => {
        const currentProfile = selectedProfile;
        try {
            setSelectedProfile(value);
            await setParentProfile(
                value.role === AppRoles.PARENT ? null : value.id!
            );
        } catch (error) {
            setSelectedProfile(currentProfile);
        }
    };

    const renderWebSubscribeButton = () => {
        if (authState && showSubscribeNow) {
            return (
                <div
                    onClick={handleClickSubscribeNow}
                    className={
                        "text-aha-green py-1 px-2 border-r-2 border-aha-green cursor-pointer"
                    }
                >
                    subscribe now
                </div>
            );
        }
    };

    const renderTrialCountdown = (isMobile = false) => {
        if (authState?.user?.freeTrialEndsAt) {
            if (!freeTrialRemainingTime) return null;

            return (
                <span
                    className={
                        isMobile
                            ? "absolute right-8 top-2 md:hidden"
                            : "py-1 px-2 border-r-2 border-aha-green"
                    }
                    title="Remaining time of your free trial"
                >
                    {freeTrialRemainingTime}
                </span>
            );
        }
    };

    const renderWebPortalButton = () => {
        if (authState?.user) {
            return (
                <span
                    onClick={() =>
                        isFreeTrialEnded && !isB2cParentOrStudent(authState)
                            ? handleClickSubscribeNow()
                            : handleClickPortal("web")
                    }
                    className={
                        "inline-block w-full md:inline md:w-auto md:text-aha-black md:py-1 md:px-2 md:border-r-2 md:border-aha-green cursor-pointer"
                    }
                >
                    {getPortalText(authState?.user, false)}
                </span>
            );
        }
    };

    // Mount-only fetch of the user; getUserFromApi is recreated every render.
    useEffect(() => {
        getUserFromApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        const calculateTimeRemaining = () => {
            const freeTrial = authState?.user?.freeTrialEndsAt;
            if (!freeTrial) return;

            const now = moment().utc();
            const end = moment.utc(freeTrial);
            if (!end.isValid()) return;

            const diff = end.diff(now);
            if (diff <= 0) return;

            const duration = moment.duration(diff);
            const days = Math.floor(duration.asDays());
            const hours = duration.hours();
            const minutes = duration.minutes();

            setFreeTrialRemainingTime(
                `${days} days, ${hours} hours, ${minutes} min`
            );
        };

        calculateTimeRemaining();

        const intervalId = setInterval(calculateTimeRemaining, 60000);

        return () => clearInterval(intervalId);
    }, [authState?.user?.freeTrialEndsAt]);

    useEffect(() => {
        if (
            isFreeTrialEnded &&
            location.pathname.includes("/app") &&
            !location.pathname.includes("/app/admin-portal")
        ) {
            setShowSubscriptionModal(true);
        }

        return () => {
            setShowSubscriptionModal(false);
        };
    }, [isFreeTrialEnded, location]);

    return (
        <>
            <div className={"border-b-12 py-4 border-aha-green"}>
                <div
                    className={
                        "flex container gap-10  justify-between items-end relative"
                    }
                >
                    <div className={"flex"}>
                        <Link to={authState.token ? "/app/lets-play" : "/"}>
                            <img
                                src={logoImage}
                                alt="At Home Articulation Logo"
                                className={"w-32"}
                            />
                        </Link>
                        <div
                            className="cursor-pointer space-y-2 ml-10 mt-10"
                            onClick={openSideBar}
                        >
                            <span className="block h-0.5 w-8 animate-pulse bg-gray-600"></span>
                            <span className="block h-0.5 w-8 animate-pulse bg-gray-600"></span>
                            <span className="block h-0.5 w-8 animate-pulse bg-gray-600"></span>
                        </div>
                    </div>

                    {renderTrialCountdown(true)}

                    {/* MOBILE HEADER */}
                    <div className="md:hidden">
                        <WebLoginOutButtons
                            user={authState?.user}
                            menuItems={getMenuItems(true)}
                        />
                    </div>

                    {/* WEB HEADER */}
                    <div
                        className={
                            "flex-1 items-center justify-end gap-2 tracking-widest hidden md:flex"
                        }
                    >
                        {renderTrialCountdown()}

                        {renderWebSubscribeButton()}

                        {renderWebPortalButton()}

                        <WebLoginOutButtons
                            user={authState?.user}
                            menuItems={getMenuItems()}
                        />
                    </div>
                </div>
            </div>

            {showSubscriptionModal && (
                <SubscriptionModal
                    skipRoleDescription={true}
                    showModal={showSubscriptionModal}
                    onClose={() => setShowSubscriptionModal(false)}
                />
            )}
        </>
    );
};

export default Header;
